import { Component, ElementRef, ViewChild, Input, Output, EventEmitter } from '@angular/core';

import { ICESCell, Factory, Release, Recapture, Catch, Color, Filter, ICESFilter, FeatureData, FeatureTypes, LayerTypes, FeatureStates, InterpolatedFeatureStyle } from '../../core/models';
import { FactoriesHelper, ReleasesHelper, RecapturesHelper, CatchesHelper } from '../../core/helpers';
import { AppSettings, LoggerService, SettingsService, FishMapService } from '../../services';
import { FilterRecapture } from '@app/core/models/common/filter-recapture';

declare var ol: any;
@Component({
    selector: 'app-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss']
})
export class MapComponent {
    @ViewChild('mapElement', { static: true }) mapElement: ElementRef;
    @ViewChild('popupElement', { static: true }) popupElement: ElementRef;

    // Input fields
    private _showICESGrid: boolean;
    private _showFactories: boolean;
    private _releasesFilter: Filter;
    private _recapturesFilter: FilterRecapture;
    private _catchesFilter: Filter;
    private _activeLayer: LayerTypes;
    private _factories: Factory[];
    private _releases: Release[];
    private _recaptures: Recapture[];
    private _catches: Catch[];
    private _icesCells: ICESCell[];
  
    // Input fields setters
    @Input()
    set showICESGrid(value: boolean) {
        this._showICESGrid = value;
        this.setLayerVisibility(this.icesGridLayer, value);
    }

    @Input()
    set showFactories(value: boolean) {
        this._showFactories = value;
        this.setLayerVisibility(this.factoriesLayer, value);
    }

    @Input()
    set activeLayer(layer: LayerTypes) {
        this._activeLayer = layer;
        if (layer) {
            this._activeLayer = Number(layer);
            this.changeActiveLayer();
        }
    }

    @Input()
    set icesCells(cells: ICESCell[]) {
        this._icesCells = cells;
        if (cells) {
            this.loadICESGrid();
        }
    }

    @Input()
    set factories(factories: Factory[]) {
        this._factories = factories;
        if (factories) {
            this.factoriesHelper.factories = factories;
            this.filterFactories([]);
        }
    }

    @Input()
    set releases(releases: Release[]) {
        this._releases = releases;
        this.releasesHelper.releases = releases;
    }

    @Input()
    set catches(catches: Catch[]) {
        this._catches = catches;
        this.catchesHelper.catches = catches;
    }

    @Input()
    set recaptures(recaptures: Recapture[]) {
        this._recaptures = recaptures;
        this.recapturesHelper.recaptures = recaptures;
        if (this._recapturesFilter) {
            this.filterRecaptures(this._recapturesFilter);
        }
        // Releases should be loaded after recaptures due to its dependency
        if (this._releasesFilter) {
            this.filterReleases(this._releasesFilter);
        }
        // Catches also should be loaded after recaptures due to its dependency
        if (this._catchesFilter) {
            this.filterCatches(this._catchesFilter);
        }
    }

    @Input()
    set releasesFilter(filter: Filter) {
        this._releasesFilter = filter;
        if (this._releases && this._releases.length > 0) {
            this.filterReleases(filter);
        }
    }

    @Input()
    set catchesFilter(filter: Filter) {      
        this._catchesFilter = filter;
        if (filter) {
            this.filterFactories(filter.Factory);
        }
        if (this._catches && this._catches.length > 0) {
            this.filterCatches(filter);
        }
    }

    @Input()
    set recapturesFilter(filter: FilterRecapture) {
        this._recapturesFilter = filter;
        if (filter) {
            this.filterFactories(filter.Factory);
        }
        if (this._recaptures && this._recaptures.length > 0) {
            this.filterRecaptures(filter);
        }
    }

    // Outputs
    @Output()
    onSelectedFeatureChange = new EventEmitter<FeatureData>();

    @Output()
    onTotalChange = new EventEmitter<FeatureData>();

    // fileds
    map: any;
    selectedFeature: any;
    markedFeature: any;
    markedFeatures: any[];
    popup: any;

    // Map layers
    icesGridLayer: any;
    factoriesLayer: any;
    highlightLayer: any;
    catchesLayer: any;
    releasesLayer: any;
    recapturesLayer: any;

    // Helpers
    factoriesHelper: FactoriesHelper;
    releasesHelper: ReleasesHelper;
    recapturesHelper: RecapturesHelper;
    catchesHelper: CatchesHelper;

    constructor(
        private appSettings: AppSettings,
        private logger: LoggerService,
        private fishMap: FishMapService
    ) {
        this.icesCells = [];
        this.markedFeatures = [];

        this.factoriesHelper = new FactoriesHelper();
        this.releasesHelper = new ReleasesHelper();
        this.recapturesHelper = new RecapturesHelper();
        this.catchesHelper = new CatchesHelper();
    }

    ngOnInit() {
        this.initializeMap();
    }

    updateMapSize(): void {
        if (this.map) {
            setTimeout(() => this.map.updateSize(), 10);
        }
    }

    initializeMap() {
        this.mapElement.nativeElement.innerHTML = '';
        this.map = new ol.Map({
            layers: [
                new ol.layer.Tile({
                    source: new ol.source.XYZ({
                        crossOrigin: null,
                        //url: 'http://{a-c}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
                        url: 'https://cartodb-basemaps-{a-c}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png',
                        attributions: [ ol.source.OSM.ATTRIBUTION, '© <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors.'],
                    })
                })
            ],
            // interactions: ol.interaction.defaults({
            //     mouseWheelZoom: false,
            // }),
            view: new ol.View({
                center: ol.proj.transform([5, 58], 'EPSG:4326', 'EPSG:3857'),
                zoom: 4.2,
            })
        });
        // Add popup
        this.popup = new ol.Overlay({
            element: this.popupElement.nativeElement,
            offset: [0, -5],
            positioning: 'bottom-center'
        })
        this.map.addOverlay(this.popup);
        this.map.setTarget(this.mapElement.nativeElement);
        this.addFeatureClickHandler();
        this.addFeatureHoverHandler();

        this.addLayers();

        this.updateMapSize();
    }

    // Add required layers to the map
    addLayers(): void {
        this.icesGridLayer = this.getEmptyLayer(true, this.getICESGridStyle());
        // this.factoriesLayer = this.getEmptyLayer(true, this.getFactoryPointStyle());
        this.factoriesLayer = this.getEmptyLayer(true);
        this.releasesLayer = this.getEmptyLayer(true);
        this.recapturesLayer = this.getEmptyLayer(false);
        this.catchesLayer = this.getEmptyLayer(false);
        this.highlightLayer = this.getEmptyLayer(true);

        this.map.addLayer(this.icesGridLayer);
        this.map.addLayer(this.releasesLayer);
        this.map.addLayer(this.recapturesLayer);
        this.map.addLayer(this.catchesLayer);
        this.map.addLayer(this.factoriesLayer);
        this.map.addLayer(this.highlightLayer);
    }

    // add click event handler to features
    addFeatureClickHandler(): void {
        if (this.map) {
            let onClick = (data) => this.showRegionStatistics(data);
            this.map.on("click", (event) => {
                let features = [];
                this.map.forEachFeatureAtPixel(event.pixel, function (feature, layer) {
                    features.push(feature);
                });
                if (features.length > 0) {
                    onClick(features[0]);
                }
            });
        }
    }

    // call show statistics for highlighted feature
    showRegionStatistics(feature: any): void {
        let data: FeatureData = feature.get('data');
        if (data.FeatureType != FeatureTypes.ICES && data.FeatureType != FeatureTypes.Factory) {
            this.highlightCell(feature);
            this.onSelectedFeatureChange.emit(data);
        }
    }

    // add hover event handler to features
    addFeatureHoverHandler(): void {
        if (this.map) {
            let onHover = (data) => this.markHoveredFeature(data);
            this.map.on('pointermove', (event) => {
                let features = [];
                this.popup.setPosition(event.coordinate);
                this.map.forEachFeatureAtPixel(event.pixel, function (feature, layer) {
                    features.push(feature);
                });
                // Remove previous marked features
                this.removeMarks();
                for (let feature of features) {
                    onHover(feature);
                }
            });
        }
    }

    // draw a thin border around features which are hovered by mouse
    markHoveredFeature(feature: any): void {
        let data: FeatureData = feature.get('data');
        if (data.FeatureType == FeatureTypes.Factory) {
            this.markFactory(feature);
        }
        else if (data.FeatureType != FeatureTypes.ICES) {
            this.markCell(feature)
        }
    }

    // mark factory
    markFactory(feature: any): void {

        let style = feature.getStyle();
        let data: FeatureData = feature.get('data');
        data.State = FeatureStates.Hovered;
        feature.set('data', data);
        feature.setStyle(style);

        this.popupElement.nativeElement.style.display = '';
        this.popupElement.nativeElement.innerHTML = `<span>${data.Data.Name}</span>`;

        // Save into selected feature
        this.addToMarkedFeatures(feature);

        // this.addFeaturesToLayer(this.highlightLayer, [feature]);
    }

    // mark cell
    markCell(feature: any): void {

        let data: FeatureData = feature.get('data');
        if (data.State != FeatureStates.Highlighted) {
            let style = feature.getStyle();
            data.State = FeatureStates.Hovered;
            feature.set('data', data);
            feature.setStyle(style);

            // Save into selected feature
            this.addToMarkedFeatures(feature);
        }
    }

    addToMarkedFeatures(feature: any): void {
        let existing = this.markedFeatures.find(f => f == feature);
        if (!existing) {
            this.markedFeatures.push(feature);
        }
    }

    removeMarks(): void {

        for (let feature of this.markedFeatures) {
            let data: FeatureData = feature.get('data');
            if (data.State != FeatureStates.Highlighted) {
                this.popupElement.nativeElement.style.display = 'none';

                let style = feature.getStyle();
                data.State = FeatureStates.Default;
                feature.set('data', data);
                feature.setStyle(style);
            }
        }
        this.markedFeatures = [];
    }

    // highlight factory
    highlightFactory(feature: any): void {
        // Remove previous
        this.removeHighlight();
        // Save into selected feature
        this.selectedFeature = feature;

        // let style = this.getHighlightedFactoryPointStyle();
        // feature.setStyle(style);
        this.addFeaturesToLayer(this.highlightLayer, [feature]);

    }

    // highlight cell
    highlightCell(feature: any): void {
        // Remove previous
        this.removeHighlight();
        let style = feature.getStyle();

        // Set highlighted feature to true
        let data: FeatureData = feature.get('data');
        data.State = FeatureStates.Highlighted;
        this.selectedFeature = feature;
        feature.set('data', data);
        feature.setStyle(style);

        this.addFeaturesToLayer(this.highlightLayer, [feature]);
    }

    // remove highlighted feature
    removeHighlight(): void {
      
        if (this.selectedFeature) {
            let data: FeatureData = this.selectedFeature.get('data');
            data.State = FeatureStates.Default;
            this.highlightLayer.getSource().clear();
            if (data.FeatureType == FeatureTypes.Factory) {
                // let style = this.getFactoryPointStyle();
                // this.selectedFeature.setStyle(style);
            }
            else {
                let style = this.selectedFeature.getStyle();
                data.State = FeatureStates.Default;
                this.selectedFeature.set('data', data);
                this.selectedFeature.setStyle(style);
            }
        }
    }

    // change the visible layer
    changeActiveLayer(): void {
      
        // Remove any highlighted feature
        this.removeHighlight();
        let selectedFactoryIds = [];
        // Make the active layer visible
        switch (this._activeLayer) {
            case LayerTypes.Releases:
                this.setLayerVisibility(this.releasesLayer, true);
                this.setLayerVisibility(this.recapturesLayer, false);
                this.setLayerVisibility(this.catchesLayer, false);
                break;
            case LayerTypes.Recaptures:
                selectedFactoryIds = this._recapturesFilter ? this._recapturesFilter.Factory : [];
                this.setLayerVisibility(this.releasesLayer, false);
                this.setLayerVisibility(this.recapturesLayer, true);
                this.setLayerVisibility(this.catchesLayer, false);
                break;
            case LayerTypes.Catches:
                selectedFactoryIds = this._catchesFilter ? this._catchesFilter.Factory : [];
                this.setLayerVisibility(this.releasesLayer, false);
                this.setLayerVisibility(this.recapturesLayer, false);
                this.setLayerVisibility(this.catchesLayer, true);
                break;
        }
        this.filterFactories(selectedFactoryIds);
    }

    // Load ICES cells grid
    loadICESGrid(): void {
        if (this._icesCells) {
            let features = [];
            for (let ices of this._icesCells) {
                let feature = this.getPolygonFeature(ices.Coordinates, new FeatureData(FeatureTypes.ICES, ices));
                let data = new FeatureData(FeatureTypes.ICES, ices);
                // feature.setStyle(this.getNamedICESGridStyle(data));
                features.push(feature);
            }
            // Draw grid lines in the map
            this.addFeaturesToLayer(this.icesGridLayer, features);
        }
    }

    // Filter factories in the map
    filterFactories(factory: string[]): void {
        let features = [];
        if (factory.length > 0) {
            let factories = this.factoriesHelper.findFactory(factory);
           for(let f of factories) {
            let feature = this.getIconFeature([f.Longitude, f.Latitude], new FeatureData(FeatureTypes.Factory, f));
            feature.setStyle(this.getFactoryPointStyle());
            features.push(feature);
           }           
        }
        else {
            for (let factory of this.factoriesHelper.factories) {
                let feature = this.getIconFeature([factory.Longitude, factory.Latitude], new FeatureData(FeatureTypes.Factory, factory));
                feature.setStyle(this.getFactoryPointStyle());
                features.push(feature);
            }
        }
        this.addFeaturesToLayer(this.factoriesLayer, features, true);
    }

    // Filter releases in the map
    filterReleases(filter: Filter): void {
        let releases = [];
        releases = this.releasesHelper.findReleases(filter);
        this.drawReleases(releases, true);
    }

    drawReleases(releases: Release[], redraw: boolean = false): void {
        let features = [];
        let total = 0;
        if (releases.length > 0) {
            // Group releases by ICES cell
            let groups = this.releasesHelper.groupByICES(releases);
            // Fill corresponding recaptures
            for (let group of groups) {
                let recaptures = this.recapturesHelper.findRecapturesByReleasedICES(new ICESFilter(group.ICES, this._releasesFilter.Year));
                let recapturesGroups = this.recapturesHelper.getAccumulatedRecapturesByICES(recaptures);
                group.Recaptures = recapturesGroups;
                total += group.Total;
            }
            if (groups.length > 0) {
                // Get maximum
                let max = this.releasesHelper.getMaxReleaseGroup(groups);
                // Get minimum
                let min = this.releasesHelper.getMinReleaseGroup(groups);
                // Draw releases with respect to theri ICES cell
                for (let group of groups) {
                    let color = new Color(0, 132, 255);//#0084FF // new Color(118, 196, 229); //#76C4E5
                    let data = new FeatureData(FeatureTypes.Releases, group);
                    let feature = this.getInterpolatedFeature(color, data, min.Total, max.Total);
                    if (feature) {
                        features.push(feature);
                    }
                }
            }
        }
        this.onTotalChange.emit(new FeatureData(FeatureTypes.Releases, total));
        this.addFeaturesToLayer(this.releasesLayer, features, redraw);
        // this.fitContentToFeatures(this.releasesLayer);
    }

    // Filter recaptures in the map
    filterRecaptures(filter: FilterRecapture) {
        let recaptures = [];
        if (filter.Year > 0 || filter.Factory.length > 0 || filter.ReleaseYear > 0 || filter.Expedition.length > 0) {
            recaptures = this.recapturesHelper.findRecaptures(filter);
        }
        else {
            recaptures = this.recapturesHelper.recaptures;
        }
        this.drawRecaptures(recaptures, true);
    }

    drawRecaptures(recaptures: Recapture[], redraw: boolean = false): void {
        let features = [];
        let total = 0;
        if (recaptures.length > 0) {
            // Group recaptures by ICES cell
            let groups = this.recapturesHelper.groupByICES(recaptures);
            // Fill corresponding releases
            for (let group of groups) {
                let filter = new ICESFilter(group.ICES, this._recapturesFilter.Year, this._recapturesFilter.Factory);
                let releases = this.recapturesHelper.findReleasesByICES(filter);
                let releasesGroups = this.recapturesHelper.getAccumulatedReleasesByICES(releases);
                group.Releases = releasesGroups;
                total += group.Total;
            }
            if (groups.length > 0) {
                // Get maximum
                let max = this.recapturesHelper.getMaxRecaptureGroup(groups);
                // Get minimum
                let min = this.recapturesHelper.getMinRecaptureGroup(groups);
                // Draw releases with respect to theri ICES cell
                for (let group of groups) {
                    let color = new Color(196, 60, 232); //#C43CE8
                    let data = new FeatureData(FeatureTypes.Recaptures, group);
                    let feature = this.getInterpolatedFeature(color, data, min.Total, max.Total);
                    if (feature) {
                        features.push(feature);
                    }
                }
            }
        }
        this.onTotalChange.emit(new FeatureData(FeatureTypes.Recaptures, total));
        this.addFeaturesToLayer(this.recapturesLayer, features, true);
    }


    // Filter catches in the map
    filterCatches(filter: Filter) {
        let catches = [];
        if (filter.Year != 0 || filter.Factory.length > 0) {
            catches = this.catchesHelper.findCatches(filter);
        }
        else {
            catches = this.catchesHelper.catches;
        }
        this.drawCatches(catches, true);
    }

    drawCatches(catches: Catch[], redraw: boolean = false): void {
        let features = [];
        let total = 0;
        if (catches?.length > 0) {
            // Group catches by ICES cell
            let groups = this.catchesHelper.groupByICES(catches);
            // Fill corresponding releases
            for (let group of groups) {
                let filter = new ICESFilter(group.ICES, this._catchesFilter.Year, this._catchesFilter.Factory);
                let recaptures = this.recapturesHelper.findRecapturesByRecapturedICES(filter);
                let recapturesGroups = this.recapturesHelper.getAccumulatedReleasesByICES(recaptures);
                group.Recaptures = recapturesGroups;
                total += group.Total;
            }
            if (groups.length > 0) {
                // Get maximum
                let max = this.catchesHelper.getMaxCatchGroup(groups);
                // Get minimum
                let min = this.catchesHelper.getMinCatchGroup(groups);
                // Draw releases with respect to theri ICES cell
                for (let group of groups) {
                    let color = new Color(22, 174, 0); //#16AE00
                    let data = new FeatureData(FeatureTypes.Catches, group);
                    let feature = this.getInterpolatedFeature(color, data, min.Total, max.Total);
                    if (feature) {
                        features.push(feature);
                    }
                }
            }
        }
        this.onTotalChange.emit(new FeatureData(FeatureTypes.Catches, total));
        this.addFeaturesToLayer(this.catchesLayer, features, true);
    }

    /* Feature styles */

    private getInterpolatedStyleConfig(color: Color, currentValue: number, minValue: number, maxValue: number): InterpolatedFeatureStyle {
        let styleConfig: InterpolatedFeatureStyle = {
            Color: color,
            MaxOpacity: 1,
            MinOpacity: 0.3,
            CurrentValue: currentValue,
            MinValue: minValue,
            MaxValue: maxValue
        };
        return styleConfig;
    }

    private getICESGridStyle(): any {
        let style = new ol.style.Style({
            stroke: new ol.style.Stroke({
                color: 'rgba(238, 238, 238, 0.5)', // '#EEE',
                width: 0.5
            })
        });
        return style;
    }

    private getNamedICESGridStyle(data: FeatureData): any {
        let style = () => {
            let zoom = this.map.getView().getZoom();
            let scale = (zoom - 4) * 0.5;
            let fontSize = scale * 12;
            return [new ol.style.Style({
                stroke: new ol.style.Stroke({
                    color: 'rgba(238, 238, 238, 0.5)', // '#EEE',
                    width: 0.5
                }),
                text: new ol.style.Text({
                    font: `${fontSize}px Roboto, "Helvetica Neue", sans-serif`,
                    fill: new ol.style.Fill({ color: 'rgba(0, 0, 0, 0.4)' }),
                    text: fontSize < 12 ? '' : data.Data.ICESName
                }),
            })];
        };
        return style;
    }

    private getStrokeStyle(color: string, width: number): any {
        let stroke = new ol.style.Stroke({
            color: color,
            width: width
        });
        return stroke;
    }

    // private getFactoryPointStyle(): any {
    //     let style = new ol.style.Style({
    //         image: new ol.style.Circle({
    //             radius: 5,
    //             fill: new ol.style.Fill({
    //                 color: '#5DA514'
    //             })
    //         })
    //     });
    //     return style;
    // }

    // getInterpolatedFeatureStyle(style: InterpolatedFeatureStyle): any {
    //     let difference = Math.abs(style.MaxValue - style.MinValue + 1);
    //     let opacityDifference = style.MaxOpacity - style.MinOpacity;
    //     let scaledOpacity = '1';
    //     if (difference > 0) {
    //         scaledOpacity = ((opacityDifference / difference) * style.CurrentValue + style.MinOpacity).toFixed(2);
    //     }
    //     // console.log(style.MinValue, style.MaxValue);
    //     // console.log('Value: ' + style.CurrentValue + ', scaledOpacity: ' + scaledOpacity);
    //     let color = `rgba(${style.Color.r}, ${style.Color.g}, ${style.Color.b}, ${scaledOpacity})`;
    //     let featureStyle = new ol.style.Style({
    //         fill: new ol.style.Fill({
    //             color: color
    //         })
    //     });
    //     return featureStyle;
    // }

    private getFactoryPointStyle(): any {
        let style = (feature, resolution) => {
            let data: FeatureData = feature.get('data');
            let zoom = this.map.getView().getZoom();
            let scale = (zoom - 4) * 0.5;
            let fontSize = scale * 12;
            let strokeWidth = 0;
            let zIndex = 100;
            if (data.State == FeatureStates.Hovered) {
                strokeWidth = 2;
                zIndex = 500;
            }
            return [new ol.style.Style({
                image: new ol.style.Circle({
                    radius: 5,
                    fill: new ol.style.Fill({
                        color: '#5DA514'
                    }),
                    stroke: strokeWidth == 0 ? null : new ol.style.Stroke({
                        color: '#000',
                        width: strokeWidth
                    })
                }),
                text: new ol.style.Text({
                    // font: `${ fontSize }px Roboto, "Helvetica Neue", sans-serif`,
                    font: `10px Roboto, "Helvetica Neue", sans-serif`,
                    offsetX: 10,
                    offsetY: 0,
                    textAlign: 'left',
                    placement: 'point',
                    fill: new ol.style.Fill({ color: 'rgba(0, 0, 0, 0.8)' }),
                    text: zoom < 7 ? '' : data.Data.Name
                }),
                zIndex: zIndex
            })];
        }
        return style;
    }

    getInterpolatedFeatureStyle(style: InterpolatedFeatureStyle, data: FeatureData): any {
        let difference = Math.abs(style.MaxValue - style.MinValue + 1);
        let opacityDifference = style.MaxOpacity - style.MinOpacity;
        let scaledOpacity = '1';
        if (difference > 0) {
            scaledOpacity = ((opacityDifference / difference) * style.CurrentValue + style.MinOpacity).toFixed(2);
        }
        // console.log(style.MinValue, style.MaxValue);
        // console.log('Value: ' + style.CurrentValue + ', scaledOpacity: ' + scaledOpacity);
        let color = `rgba(${style.Color.r}, ${style.Color.g}, ${style.Color.b}, ${scaledOpacity})`;
        let featureStyle = (feature, resolution) => this.getInterpolatedICESStyle(feature, resolution, color);
        return featureStyle;
    }

    getInterpolatedICESStyle(feature: any, resolution: number, color: string) {
        let data: FeatureData = feature.get('data');
        let zoom = this.map.getView().getZoom();
        let scale = (zoom - 4) * 0.5;
        let fontSize = scale * 12;
        let strokeWidth = 0;
        let zIndex = 100;
        if (data.State == FeatureStates.Hovered) {
            strokeWidth = 2;
        }
        else if (data.State == FeatureStates.Highlighted) {
            strokeWidth = 3;
        }
        return [new ol.style.Style({
            fill: new ol.style.Fill({
                color: color
            }),
            stroke: strokeWidth == 0 ? null : new ol.style.Stroke({
                color: '#000',
                width: strokeWidth
            }),
            text: new ol.style.Text({
                font: `${fontSize}px Roboto, "Helvetica Neue", sans-serif`,
                fill: new ol.style.Fill({ color: 'rgba(255, 255, 255, 0.5)' }),
                text: fontSize < 12 ? '' : data.Data.ICES
            }),
        })];
    }

    /* Helper functions */

    private findICESCell(icesName: string): ICESCell {
        let cell = this._icesCells.find(i => i.ICESName == icesName);
        return cell;
    }

    private getPolygonFeature(coordinates: any[], data: FeatureData): any {
        let feature = new ol.Feature({
            geometry: new ol.geom.Polygon(coordinates),
            data: data
        });
        feature.getGeometry().transform('EPSG:4326', 'EPSG:3857');
        return feature;
    }

    private getIconFeature(point: [number, number], data?: any): any {
        let feature = new ol.Feature({
            geometry: new ol.geom.Point(ol.proj.transform(point, 'EPSG:4326', 'EPSG:3857')),
            data: data
        });
        return feature;
    }

    private getEmptyLayer(visible: boolean, style?: any): any {
        let source = new ol.source.Vector({
            features: []
        });
        let layer = new ol.layer.Vector({
            source: source,
            style: style,
            visible: visible
        });
        return layer;
    }

    // Get interpolated feature
    private getInterpolatedFeature(color: Color, data: FeatureData, min: number, max: number): any {
        let feature;
        let icesCell = this.findICESCell(data.Data.ICES);
        if (icesCell) {
            feature = this.getPolygonFeature(icesCell.Coordinates, data);
            let styleConfig = this.getInterpolatedStyleConfig(color, data.Data.Total, min, max);
            let style = this.getInterpolatedFeatureStyle(styleConfig, data);
            feature.setStyle(style);
        }
        return feature;
    }

    private setLayerVisibility(layer: any, value: boolean): void {
        if (layer) {
            layer.setVisible(value);
        }
    }

    private addFeaturesToLayer(layer: any, features: any[], redraw: boolean = false): void {
        if (layer) {
            let source = layer.getSource();
            if (source) {
                if (redraw) {
                    source.clear();
                }
                for (let feature of features) {
                    source.addFeature(feature);
                }
            }
        }
    }

    private fitContentToFeatures(layer: any): void {
        if (layer) {
            let source = layer.getSource();
            if (source) {
                this.map.getView().fit(source.getExtent(), this.map.getSize(), { maxZoom: 10 });
            }
        }
    }

}
